import '../../themes/global.scss';
import '../../themes/job_summary.scss';
import '../../themes/payment.scss';

import { useState, type CSSProperties } from 'react';
import { Alert, Column, Row, Screen, Typography , CircularProgress } from '@tempmee/react';
import { colors } from '@tempmee/tailwind';
import { BackButton } from '~/commonComponents';
import { useAppSelector } from '~/hooks';
import { OutlineLockClosedIcon } from '@tempmee/react-icons';

import { useCheckPaymentExpiration, useGetPaymentMethods } from '~/domain/Payments';
import { BankList } from './components/BankList';
import { CardList } from './components/CardList';
import { PendingAchVerificationBanner } from './PendingAchVerificationBanner';
import { SuspendedPostingsPopup } from './SuspendedPostingsPopup';

const officeBarStyle: CSSProperties = {
  backgroundColor: colors.neutral[100],
  height: 52,
  marginBottom: 4,
};

export default function Payment() {
  const [selectedPayment, setSelectedPayment] = useState<Record<string, unknown> | null>(null);
  const { isLoading } = useGetPaymentMethods();
  const selectedChildOffice = useAppSelector((state) => state.user.selectedChildOffice);
  const childOffices = useAppSelector((state) => state.user.childOffices);

  const { isExpired, isExpiring } = useCheckPaymentExpiration();

  return (
    <>
      {/* Stack the banner above the Screen. The route renders us inside a
          horizontal Row, so we need our own Column to stack vertically and
          still let the Screen grow to fill the remaining space. */}
      <Column style={{ flex: 1, minWidth: 0, backgroundColor: colors.neutral[50] }}>
        <PendingAchVerificationBanner
          dataCy="payment-pending-ach-warning"
          // Align with the Screen card: 16px = the Screen's default `p-4` padding.
          // The DS Ribbon is `w-full`, so a right margin alone overflows instead
          // of insetting — shrink the width to make room for both side margins.
          containerStyle={{
            width: 'calc(100% - 32px)',
            marginTop: 16,
            marginRight: 16,
            marginLeft: 16,
            marginBottom: 0,
          }}
        />

        <Screen variant="standard">
          <Screen.Header start={<BackButton />} title="Payment Information">
            <Row align="center" justify="center" gap={1}>
              <Typography italic color={colors.neutral[500]}>
                GoTu works with Stripe &amp; Plaid to secure your payment information.
              </Typography>
              <OutlineLockClosedIcon color={colors.neutral[500]} />
            </Row>
          </Screen.Header>
          <Screen.Body inset>
            <Column gap={3} style={{ minHeight: 400 }}>
              {isExpired && (
              <Alert
                severity="warning"
                variant="filled"
                description="Your default payment method has expired. Please update your payment method to confirm future professionals."
              />
            )}

              {isExpiring && (
              <Alert
                severity="warning"
                variant="standard"
                description="Your default payment method is about to expire. Please update your payment method to confirm future professionals."
              />
            )}

              {childOffices.length > 1 && (
              <Row align="center" pl={8} style={officeBarStyle}>
                <Typography variant="body1" bold color={colors.neutral[600]}>
                  Office Name:{' '}
                  <Typography as="span" variant="body1" color={colors.neutral[600]}>
                    {selectedChildOffice?.office_name}
                  </Typography>
                </Typography>
              </Row>
            )}

              {isLoading && (
              <Row
                align="center"
                justify="center"
                style={{ width: '100%', flex: 1, minHeight: 300 }}
              >
                <CircularProgress />
              </Row>
            )}

              {!isLoading && (
              <>
                <CardList
                  selectedPayment={selectedPayment}
                  setSelectedPayment={setSelectedPayment}
                />
                <BankList
                  selectedPayment={selectedPayment}
                  setSelectedPayment={setSelectedPayment}
                />
              </>
            )}
            </Column>
          </Screen.Body>
        </Screen>
      </Column>
      <SuspendedPostingsPopup />
    </>
  );
}
